import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BrilloLogo from "@/components/BrilloLogo";
import api, {
  TIPO_OPERACION_OPTIONS,
  TIPO_CREDITO_OPTIONS,
  VALOR_OPERACION_OPTIONS,
  labelOf,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  DollarSign,
  MessageSquare,
  TrendingUp,
  Download,
  Search,
  LogOut,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const ALL_TIPO_OPTIONS = [
  ...TIPO_OPERACION_OPTIONS,
  ...TIPO_CREDITO_OPTIONS.filter(
    (o) => !TIPO_OPERACION_OPTIONS.some((n) => n.value === o.value)
  ),
];

const fmtCurrency = (v) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
};

const getTipo = (l) => l.tipo_operacion || l.tipo_credito || "";
const getTipoLabel = (l) => {
  const v = getTipo(l);
  return labelOf(TIPO_OPERACION_OPTIONS, v) || labelOf(TIPO_CREDITO_OPTIONS, v) || v;
};
const getValor = (l) => {
  if (l.valor_operacion) return labelOf(VALOR_OPERACION_OPTIONS, l.valor_operacion);
  if (l.monto) return fmtCurrency(l.monto);
  return "-";
};
const getTelefono = (l) => l.telefono || l.whatsapp || "-";

/* ——— Sub-components ——— */

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="rounded-2xl border border-[#e5e7eb] bg-white p-5" data-testid={testId}>
      <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <div className="mt-4 font-display text-2xl text-[#333333]">{value}</div>
      <div className="text-xs text-[#666666] mt-1 uppercase tracking-[0.15em]">{label}</div>
    </div>
  );
}

function FilterBar({ q, setQ, tipoFilter, setTipoFilter, valorFilter, setValorFilter, onExport }) {
  return (
    <div className="p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between border-b border-[#e5e7eb]">
      <div className="flex flex-col md:flex-row gap-3 md:items-center flex-1">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, email o teléfono"
            className="pl-9"
            data-testid="admin-search-input"
          />
        </div>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="max-w-[220px]" data-testid="filter-tipo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {ALL_TIPO_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={valorFilter} onValueChange={setValorFilter}>
          <SelectTrigger className="max-w-[220px]" data-testid="filter-valor">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los valores</SelectItem>
            {VALOR_OPERACION_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        onClick={onExport}
        data-testid="admin-export-csv"
        className="inline-flex items-center gap-2 rounded-full bg-[#333333] text-white px-4 py-2 text-sm hover:bg-black transition-colors"
      >
        <Download className="h-4 w-4" /> Exportar CSV
      </button>
    </div>
  );
}

function LeadsTable({ leads, loading }) {
  if (loading) {
    return (
      <div className="p-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#04b0ba]" />
      </div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Fecha</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead>Teléfono</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Valor</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {leads.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-10 text-[#666666]">
              Aún no hay prospectos que coincidan.
            </TableCell>
          </TableRow>
        ) : (
          leads.map((l) => (
            <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
              <TableCell className="text-xs text-[#666666]">{fmtDate(l.created_at)}</TableCell>
              <TableCell className="font-medium">{l.nombre}</TableCell>
              <TableCell>
                <a
                  href={`https://wa.me/${getTelefono(l).replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bf-link"
                >
                  {getTelefono(l)}
                </a>
              </TableCell>
              <TableCell>
                <a href={`mailto:${l.email}`} className="bf-link">{l.email}</a>
              </TableCell>
              <TableCell>{getTipoLabel(l)}</TableCell>
              <TableCell>{getValor(l)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function ContactsTable({ contacts, loading }) {
  if (loading) {
    return (
      <div className="p-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#04b0ba]" />
      </div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Fecha</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Mensaje</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contacts.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} className="text-center py-10 text-[#666666]">
              Sin mensajes por ahora.
            </TableCell>
          </TableRow>
        ) : (
          contacts.map((c) => (
            <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
              <TableCell className="text-xs text-[#666666]">{fmtDate(c.created_at)}</TableCell>
              <TableCell className="font-medium">{c.nombre}</TableCell>
              <TableCell>
                <a href={`mailto:${c.email}`} className="bf-link">{c.email}</a>
              </TableCell>
              <TableCell className="max-w-[520px]">
                <p className="text-sm text-[#333333] whitespace-pre-wrap">{c.mensaje}</p>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

/* ——— Main component ——— */

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(location.state?.user || null);
  const [authChecking, setAuthChecking] = useState(!location.state?.user);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [valorFilter, setValorFilter] = useState("all");

  useEffect(() => {
    if (user) return;
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setAuthChecking(false));
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      api.get("/admin/leads"),
      api.get("/admin/contacts"),
      api.get("/admin/stats"),
    ])
      .then(([l, c, s]) => {
        setLeads(l.data.items || []);
        setContacts(c.data.items || []);
        setStats(s.data);
      })
      .catch(() => toast.error("No se pudieron cargar los datos."))
      .finally(() => setLoading(false));
  }, [user]);

  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      const lowerQ = q.toLowerCase();
      const matchesQ =
        !q ||
        (l.nombre || "").toLowerCase().includes(lowerQ) ||
        (l.email || "").toLowerCase().includes(lowerQ) ||
        getTelefono(l).includes(q);
      const matchesTipo = tipoFilter === "all" || getTipo(l) === tipoFilter;
      const matchesValor =
        valorFilter === "all" ||
        l.valor_operacion === valorFilter ||
        l.ingresos === valorFilter;
      return matchesQ && matchesTipo && matchesValor;
    });
  }, [leads, q, tipoFilter, valorFilter]);

  const exportCsv = useCallback(() => {
    const rows = [
      ["Fecha", "Nombre", "Teléfono", "Email", "Tipo de operación", "Valor / Monto"],
      ...filteredLeads.map((l) => [
        fmtDate(l.created_at),
        l.nombre,
        getTelefono(l),
        l.email,
        getTipoLabel(l),
        getValor(l),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brillo-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredLeads]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Logout API call failed silently
    }
    navigate("/login", { replace: true });
  }, [navigate]);

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#04b0ba]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BrilloLogo className="h-9 w-auto" />
            <span className="hidden md:inline text-xs uppercase tracking-[0.2em] text-[#666666]">
              Panel administrador
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden md:flex flex-col items-end">
                <span className="text-sm text-[#333333] font-medium" data-testid="admin-user-name">
                  {user.name || user.email}
                </span>
                <span className="text-xs text-[#666666]">{user.email}</span>
              </div>
            )}
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#e5e7eb] px-4 py-2 text-sm text-[#333333] hover:border-[#f05c3e] hover:text-[#f05c3e] transition-colors"
            >
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid gap-4 md:grid-cols-4">
          <StatCard icon={Users} label="Prospectos totales" value={stats?.total_leads ?? 0} accent="#f05c3e" testId="stat-total-leads" />
          <StatCard icon={TrendingUp} label="Últimos 7 días" value={stats?.last_7_days ?? 0} accent="#04b0ba" testId="stat-last-7" />
          <StatCard icon={DollarSign} label="Monto total (legacy)" value={fmtCurrency(stats?.total_monto ?? 0)} accent="#333333" testId="stat-total-monto" />
          <StatCard icon={MessageSquare} label="Mensajes de contacto" value={stats?.total_contacts ?? 0} accent="#04b0ba" testId="stat-total-contacts" />
        </div>

        <Tabs defaultValue="leads" className="mt-10">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads">Prospectos</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Mensajes</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-6">
            <div className="rounded-2xl border border-[#e5e7eb] bg-white">
              <FilterBar
                q={q}
                setQ={setQ}
                tipoFilter={tipoFilter}
                setTipoFilter={setTipoFilter}
                valorFilter={valorFilter}
                setValorFilter={setValorFilter}
                onExport={exportCsv}
              />
              <div className="overflow-x-auto">
                <LeadsTable leads={filteredLeads} loading={loading} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <div className="rounded-2xl border border-[#e5e7eb] bg-white overflow-x-auto">
              <ContactsTable contacts={contacts} loading={loading} />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
